import dynamic from 'next/dynamic';

import DraftPreviewBadge from 'layout/DraftPreviewBadge';
import Head from 'layout/Head';

import AttributionManager from 'utils/layout/AttributionManager';
import LazyRudderStackScript from 'utils/layout/LazyRudderStackScript';
import LazySegmentScript from 'utils/layout/LazySegmentScript';
import { useImmersiveChromeActive } from 'utils/layout/useImmersiveChrome';
import { TrackConvertingPage } from 'utils/trackPage';

import type { Content } from '@builder.io/react';
import type { FC } from 'react';
import type { FooterVariant, HeaderVariant, LayoutDataProps, ReactChildren } from 'types/global';

const HeaderLandingWithBuilder = dynamic(() =>
  import('layout/Landing/header/HeaderLanding').then(mod => ({ default: mod.HeaderLandingWithBuilder })),
);
const FooterVariantWrapper = dynamic(() => import('layout/Landing/footer/FooterVariantWrapper'));

const ConsentManager = dynamic(() => import('molecules/ConsentManager'), { ssr: false });

interface LandingLayoutProps extends ReactChildren, LayoutDataProps {
  seoData?: Content;
  preview?: boolean;
  headerVariant?: HeaderVariant;
  footerVariant?: FooterVariant;
}

const LandingLayout: FC<LandingLayoutProps> = ({
  children,
  seoData,
  preview,
  footerContent,
  headerContent,
  // Set by fetchLayoutModels when the chrome was fetched as a draft under
  // Next.js Preview Mode; arrives via the layoutData spread like the content.
  draftPreview,
  headerVariant,
  footerVariant = 'default',
}) => {
  // An immersive component mounted in a client-swapped A/B variation (or the
  // Builder editor preview) hides the chrome even when derivePageChrome only
  // saw the non-immersive parent variation on the server.
  const immersiveActive = useImmersiveChromeActive();
  const effectiveHeaderVariant = immersiveActive ? 'hidden' : headerVariant;
  const effectiveFooterVariant = immersiveActive ? 'hidden' : footerVariant;

  // The Connect footer snaps to the bottom of the viewport on short pages (e.g.
  // the thank-you page) via a full-height flex column, instead of sitting under
  // the skinny content.
  const isConnectFooter = effectiveFooterVariant === 'connect';

  const chrome = (
    <>
      <HeaderLandingWithBuilder content={headerContent} preview={preview} variant={effectiveHeaderVariant} />
      <main className={isConnectFooter ? 'flex-1' : undefined}>{children}</main>

      <FooterVariantWrapper
        variantFooter={effectiveFooterVariant}
        preview={preview}
        landingContent={footerContent}
        globalContent={footerContent}
      />
    </>
  );

  return (
    <>
      <Head {...(seoData as unknown as Record<string, unknown>)} />
      <AttributionManager />
      <LazySegmentScript />
      <LazyRudderStackScript />
      <TrackConvertingPage />
      {isConnectFooter ? <div className="flex min-h-screen flex-col">{chrome}</div> : chrome}

      {draftPreview && <DraftPreviewBadge />}
      <ConsentManager />
      {preview && <div className="hidden" data-preview="true" />}
    </>
  );
};

export default LandingLayout;
